/**
 * Assembles classified lines into sections and rows, then into V4 bars and
 * slots (one slot per bar: a paste never splits a bar).
 */
import { createBar } from '../barUtils.js';
import { CUSTOM_TEXT_MAX_CHARS } from '../customChordUtils.js';
import { normalizeSectionNote, normalizeSectionRepeat, SECTION_REPEAT_MAX } from '../sectionMarkers.js';
import { readRepeatCount } from './chordTokens.js';
import { slotsFromChordLine, slotsFromLyrics, wrapLyrics } from './rowBuilding.js';
const TRAILING_REPEAT = /^(.*?)\s*[-–—]?\s*(\(?(?:[x×]\s?\d{1,2}|\d{1,2}\s?[x×])\)?)$/iu;
/**
 * A header's repeat count ("Chorus x2", "Guitar Solo (x2)", "“Chorus” – 3x")
 * becomes the section's repeat. A count past what a section can hold stays in
 * the title, so nothing is lost.
 */
const splitTitleRepeat = (title) => {
    const match = title.match(TRAILING_REPEAT);
    const count = match && match[1] !== '' ? readRepeatCount(match[2]) : null;
    if (count === null || count > SECTION_REPEAT_MAX)
        return { title, repeat: null };
    return { title: match[1], repeat: normalizeSectionRepeat(count) };
};
const newSection = (title, extra = {}) => ({
    ...splitTitleRepeat(title),
    rows: [],
    note: null,
    ...extra,
});
const addNote = (section, text) => {
    section.note = section.note ? `${section.note} · ${text}` : text;
};
/**
 * A repeat count at the end of a row ("G C D G (x4)") plays the row again: it
 * becomes the section's repeat when the row is all there is, and extra copies of
 * the row otherwise. Left in place it would be a custom-text slot, a silent bar.
 */
const applyRowRepeats = (section) => {
    section.rows = section.rows.flatMap(row => {
        const last = row[row.length - 1];
        const count = row.length > 1 && last.token?.kind === 'custom' && !last.lyrics?.trim()
            ? readRepeatCount(last.token.text)
            : null;
        if (count === null)
            return [row];
        const played = row.slice(0, -1);
        const times = Math.min(Math.max(count, 1), SECTION_REPEAT_MAX);
        if (section.rows.length === 1 && section.repeat === null) {
            section.repeat = normalizeSectionRepeat(times);
            return [played];
        }
        return Array.from({ length: times }, () => played);
    });
};
/**
 * A titled header with nothing under it ("[Chorus]" again at the end, ChordPro
 * {chorus}) means "play it here": it gets the rows of the last section of that
 * name; {chorus} looks for the last {start_of_chorus} first. With nothing to recall it
 * stays empty.
 */
const recallSections = (sections) => {
    sections.forEach((section, i) => {
        if (section.rows.length > 0 || section.title === '')
            return;
        const name = section.title.toLowerCase();
        const earlier = sections.slice(0, i).reverse().filter(candidate => candidate.rows.length > 0);
        const source = (section.recallChorus ? earlier.find(candidate => candidate.chorus) : undefined)
            ?? earlier.find(candidate => candidate.title.toLowerCase() === name);
        if (!source)
            return;
        section.rows = source.rows;
        section.repeat ?? (section.repeat = source.repeat);
    });
};
/**
 * Groups lines into sections. A chord line takes the lyric line directly
 * below it. Headers start sections; when the text has no headers at all,
 * blank lines (stanza breaks) do.
 */
export const buildDraft = (lines) => {
    const draft = { title: '', keyText: null, tempo: null, timeSignature: null, sections: [], skippedLines: 0 };
    const splitOnBlankLines = !lines.some(line => line.type === 'section');
    let current = null;
    /** A note read between sections goes to the next one */
    let pendingNote = null;
    const open = (section) => {
        if (pendingNote)
            addNote(section, pendingNote);
        pendingNote = null;
        draft.sections.push(section);
        current = section;
    };
    const addRow = (slots) => {
        if (slots.length === 0)
            return;
        if (!current)
            open(newSection(''));
        current.rows.push(slots);
    };
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        switch (line.type) {
            case 'blank':
                if (splitOnBlankLines)
                    current = null;
                break;
            case 'skip':
                draft.skippedLines++;
                break;
            case 'ignored':
                break;
            case 'title':
                draft.title || (draft.title = line.value);
                break;
            case 'key':
                draft.keyText ?? (draft.keyText = line.value);
                break;
            case 'tempo':
                draft.tempo ?? (draft.tempo = line.value);
                break;
            case 'time':
                draft.timeSignature ?? (draft.timeSignature = line.value);
                break;
            case 'section':
                open(newSection(line.title, {
                    ...(line.chorus && { chorus: true }),
                    ...(line.recallChorus && { recallChorus: true }),
                }));
                break;
            case 'note':
                if (current)
                    addNote(current, line.text);
                else
                    pendingNote = pendingNote ? `${pendingNote} · ${line.text}` : line.text;
                break;
            case 'sectionEnd':
                current = null;
                break;
            case 'chords': {
                const below = lines[i + 1];
                if (below?.type === 'lyrics') {
                    addRow(slotsFromChordLine(line.tokens, below.text));
                    i++;
                }
                else {
                    addRow(slotsFromChordLine(line.tokens, null));
                }
                break;
            }
            case 'slots':
                addRow(line.slots);
                break;
            case 'lyrics':
                addRow(slotsFromLyrics(line.text));
                break;
        }
    }
    // A note after the last section belongs to it
    const last = draft.sections[draft.sections.length - 1];
    if (pendingNote && last)
        addNote(last, pendingNote);
    draft.sections.forEach(applyRowRepeats);
    recallSections(draft.sections);
    return draft;
};
/** Custom text over the limit keeps its start and an ellipsis (never splitting a surrogate pair). */
const shorten = (text, notes) => {
    if (text.length <= CUSTOM_TEXT_MAX_CHARS)
        return text;
    notes.shortenedTexts++;
    let shortened = '';
    for (const ch of Array.from(text)) {
        if (shortened.length + ch.length > CUSTOM_TEXT_MAX_CHARS - 1)
            break;
        shortened += ch;
    }
    return `${shortened.trimEnd()}…`;
};
const EMPTY = { chord: null, customText: null };
const chordArea = (token, notes) => token.kind === 'chord'
    ? { chord: copyChord(token.chord), customText: null }
    : { chord: null, customText: shorten(token.text, notes) };
/**
 * Chord area for lyrics with no chord change over them: words before a line's
 * first chord, a long fragment's continuation, a line with no chords. Playback
 * plays exactly what's written (a slot with no chord is silent), so these
 * repeat the chord sounding there. At the start of the song there is none and
 * the slot stays chordless; the editor shows its lyrics under the
 * "Tap to add chord" prompt.
 */
const continuation = (sounding) => sounding.chord ? { chord: copyChord(sounding.chord), customText: null } : EMPTY;
/** Each slot gets its own chord object, so no two slots share one. */
const copyChord = (chord) => JSON.parse(JSON.stringify(chord));
/**
 * A lyric fragment longer than a slot holds continues in slots after it, so
 * the chord change stays where it was.
 */
const materializeRow = (draft, options, notes, sounding) => {
    const slots = [];
    for (const { token, lyrics } of draft) {
        const chunks = lyrics === null ? [null] : wrapLyrics(lyrics, options.lyricsMaxChars);
        if (chunks.length > 1)
            notes.splitLyrics++;
        chunks.forEach((chunk, i) => {
            const area = i === 0 && token ? chordArea(token, notes) : chunk !== null ? continuation(sounding) : EMPTY;
            if (i === 0 && token)
                sounding.chord = area.chord;
            slots.push({ id: options.generateId('slot'), ...area, lyrics: chunk });
        });
    }
    return { id: options.generateId('row'), bars: slots.map(slot => createBar(slot, options.generateId)), timeSignature: null };
};
const emptyRow = (options) => ({
    id: options.generateId('row'),
    bars: [createBar({ id: options.generateId('slot'), ...EMPTY, lyrics: null }, options.generateId)],
    timeSignature: null,
});
/**
 * Final sections. Untitled sections with no rows are dropped; a titled one
 * with no rows (and nothing to recall) is kept with one empty slot, as a
 * marker of where it is played.
 */
export const materializeSections = (sections, options, notes) => {
    const sounding = { chord: null };
    return sections
        .filter(section => section.rows.length > 0 || section.title !== '')
        .map(section => {
        const note = normalizeSectionNote(section.note);
        return {
            id: options.generateId('section'),
            title: section.title,
            rows: section.rows.length > 0
                ? section.rows.map(row => materializeRow(row, options, notes, sounding))
                : [emptyRow(options)],
            ...(section.repeat !== null && { repeat: section.repeat }),
            ...(note !== null && { note }),
        };
    });
};
